import { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { format, addDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarIcon, DollarSign, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Calendar } from '@/components/ui/calendar'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { useProjects } from '@/hooks/useProjects'
import { useProjectPaymentSummary, useCreatePayment, useUpdatePayment, Payment, PaymentInsert, PaymentStatus, PaymentMethod } from '@/hooks/usePayments'

const paymentSchema = z.object({
  project_id: z.string().optional().nullable(),
  amount: z.coerce.number().positive('O valor deve ser maior que zero'),
  description: z.string().max(200, 'Máximo 200 caracteres').optional(),
  due_date: z.date().optional().nullable(),
  paid_date: z.date().optional().nullable(),
  status: z.enum(['pending', 'paid', 'cancelled']),
  payment_method: z.enum(['pix', 'bank_transfer', 'cash', 'boleto', 'credit_card', 'paypal', 'other']).optional().nullable(),
  invoice_number: z.string().optional(),
  notes: z.string().optional(),
  // Monthly recurring
  is_monthly_recurring: z.boolean().optional(),
  recurring_day_of_month: z.coerce.number().min(1).max(31).optional().nullable(),
})

type PaymentFormValues = z.infer<typeof paymentSchema>

interface PaymentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  payment?: Payment | null
  defaultProjectId?: string | null
}

const paymentMethodOptions = [
  { value: 'pix', label: 'PIX', icon: '💰' },
  { value: 'bank_transfer', label: 'Transferência Bancária', icon: '🏦' },
  { value: 'cash', label: 'Dinheiro', icon: '💵' },
  { value: 'boleto', label: 'Boleto', icon: '📄' },
  { value: 'credit_card', label: 'Cartão de Crédito', icon: '💳' },
  { value: 'paypal', label: 'PayPal', icon: '🌐' },
  { value: 'other', label: 'Outro', icon: '📦' },
]

export function PaymentDialog({ open, onOpenChange, payment, defaultProjectId }: PaymentDialogProps) {
  const isMobile = useIsMobile()
  const [extrasOpen, setExtrasOpen] = useState(false)
  const { data: projects } = useProjects()
  const createPayment = useCreatePayment()
  const updatePayment = useUpdatePayment()
  
  const isEditing = !!payment
  
  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      project_id: null,
      amount: 0,
      description: '',
      due_date: addDays(new Date(), 30),
      paid_date: null,
      status: 'pending',
      payment_method: null,
      invoice_number: '',
      notes: '',
      is_monthly_recurring: false,
      recurring_day_of_month: null,
    },
  })
  
  const watchProjectId = form.watch('project_id')
  const watchStatus = form.watch('status')
  const watchAmount = form.watch('amount')
  
  const { data: projectPaymentSummary } = useProjectPaymentSummary(watchProjectId || '')
  
  // Get selected project details
  const selectedProject = useMemo(() => {
    if (!watchProjectId || !projects) return null
    return projects.find(p => p.id === watchProjectId)
  }, [watchProjectId, projects])
  
  // Calculate remaining amount for fixed projects
  const remainingAmount = useMemo(() => {
    if (!selectedProject || selectedProject.billing_type !== 'fixed' || !selectedProject.fixed_price) return null
    const totalPaid = projectPaymentSummary?.total_paid || 0
    const totalPending = projectPaymentSummary?.total_pending || 0
    return Number(selectedProject.fixed_price) - totalPaid - totalPending
  }, [selectedProject, projectPaymentSummary])
  
  // Warning if payment exceeds project value
  const exceedsProjectValue = useMemo(() => {
    if (!selectedProject || selectedProject.billing_type !== 'fixed' || !selectedProject.fixed_price) return false
    const totalPaid = projectPaymentSummary?.total_paid || 0
    const totalPending = projectPaymentSummary?.total_pending || 0
    const currentPaymentAmount = payment?.amount || 0
    return (totalPaid + totalPending - currentPaymentAmount + watchAmount) > Number(selectedProject.fixed_price)
  }, [selectedProject, projectPaymentSummary, watchAmount, payment])
  
  // Reset form when dialog opens or payment changes
  useEffect(() => {
    if (open) {
      if (payment) {
        form.reset({
          project_id: payment.project_id,
          amount: Number(payment.amount),
          description: payment.description || '',
          due_date: payment.due_date ? new Date(payment.due_date) : null,
          paid_date: payment.paid_date ? new Date(payment.paid_date) : null,
          status: payment.status as 'pending' | 'paid' | 'cancelled',
          payment_method: payment.payment_method as PaymentMethod | null,
          invoice_number: payment.invoice_number || '',
          notes: payment.notes || '',
          is_monthly_recurring: !!payment.recurring_day_of_month,
          recurring_day_of_month: payment.recurring_day_of_month,
        })
      } else {
        form.reset({
          project_id: defaultProjectId || null,
          amount: 0,
          description: '',
          due_date: addDays(new Date(), 30),
          paid_date: null,
          status: 'pending',
          payment_method: null,
          invoice_number: '',
          notes: '',
          is_monthly_recurring: false,
          recurring_day_of_month: null,
        })
      }
    }
  }, [open, payment, defaultProjectId, form])
  
  const onSubmit = async (values: PaymentFormValues) => {
    const paymentData: PaymentInsert = {
      project_id: values.project_id || null,
      amount: values.amount,
      description: values.description || null,
      due_date: values.due_date ? format(values.due_date, 'yyyy-MM-dd') : null,
      paid_date: values.status === 'paid' && values.paid_date 
        ? format(values.paid_date, 'yyyy-MM-dd') 
        : values.status === 'paid' 
          ? format(new Date(), 'yyyy-MM-dd')
          : null,
      status: values.status as PaymentStatus,
      payment_method: values.payment_method || null,
      invoice_number: values.invoice_number || null,
      notes: values.notes || null,
      recurring_day_of_month: values.is_monthly_recurring ? values.recurring_day_of_month : null,
    }
    
    try {
      if (isEditing && payment) {
        await updatePayment.mutateAsync({ id: payment.id, ...paymentData })
      } else {
        await createPayment.mutateAsync(paymentData)
      }
      onOpenChange(false)
    } catch (error) {
      // Error handled by mutation
    }
  }
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value)
  }
  
  const FormContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Project Selection */}
        <FormField
          control={form.control}
          name="project_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Projeto</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                value={field.value || undefined}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um projeto (opcional)" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {projects?.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      <span className="flex items-center gap-2">
                        <span>{project.name}</span>
                        {project.clients && (
                          <span className="text-muted-foreground text-xs">
                            • {(project.clients as { name: string }).name}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Opcional para pagamentos avulsos
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Amount */}
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Valor *</FormLabel>
              <FormControl>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    R$
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0,00"
                    className="pl-10"
                    {...field}
                  />
                </div>
              </FormControl>
              {remainingAmount !== null && remainingAmount > 0 && !isEditing && (
                <FormDescription className="flex items-center justify-between">
                  <span>Valor restante do projeto: {formatCurrency(remainingAmount)}</span>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => form.setValue('amount', remainingAmount)}
                  >
                    Usar valor
                  </Button>
                </FormDescription>
              )}
              {exceedsProjectValue && (
                <div className="flex items-center gap-2 text-sm text-orange-600 mt-1">
                  <AlertTriangle className="h-4 w-4" />
                  <span>
                    Total de pagamentos excede o valor do projeto ({formatCurrency(Number(selectedProject?.fixed_price) || 0)})
                  </span>
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descrição</FormLabel>
              <FormControl>
                <Input
                  placeholder="Ex: Primeira parcela do projeto X"
                  maxLength={200}
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-right">
                {field.value?.length || 0}/200
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Due Date */}
        <FormField
          control={form.control}
          name="due_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Data de Vencimento</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !field.value && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? (
                        format(field.value, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                      ) : (
                        <span>Selecione uma data</span>
                      )}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value || undefined}
                    onSelect={field.onChange}
                    locale={ptBR}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {field.value && field.value < new Date() && watchStatus === 'pending' && (
                <FormDescription className="text-orange-600">
                  ⚠️ Data no passado - será marcado como atrasado
                </FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Status */}
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status *</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="pending" id="pending" />
                    <Label htmlFor="pending" className="flex items-center gap-1 cursor-pointer">
                      <span className="text-yellow-500">⏳</span> Pendente
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="paid" id="paid" />
                    <Label htmlFor="paid" className="flex items-center gap-1 cursor-pointer">
                      <span className="text-green-500">✅</span> Pago
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="cancelled" id="cancelled" />
                    <Label htmlFor="cancelled" className="flex items-center gap-1 cursor-pointer">
                      <span className="text-muted-foreground">❌</span> Cancelado
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Paid Date (only if status is paid) */}
        {watchStatus === 'paid' && (
          <FormField
            control={form.control}
            name="paid_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data do Pagamento</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? (
                          format(field.value, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                        ) : (
                          <span>Hoje</span>
                        )}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value || new Date()}
                      onSelect={field.onChange}
                      locale={ptBR}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        
        {/* Payment Method */}
        <FormField
          control={form.control}
          name="payment_method"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Forma de Pagamento</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                value={field.value || undefined}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {paymentMethodOptions.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      <span className="flex items-center gap-2">
                        <span>{method.icon}</span>
                        <span>{method.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Monthly Recurring Payment Toggle */}
        <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
          <FormField
            control={form.control}
            name="is_monthly_recurring"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between space-y-0">
                <div className="space-y-0.5">
                  <FormLabel className="text-base font-medium flex items-center gap-2">
                    🔄 Pagamento Recorrente Mensal
                  </FormLabel>
                  <FormDescription className="text-xs">
                    Ao pagar, cria automaticamente o próximo mês
                  </FormDescription>
                </div>
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value || false}
                    onChange={(e) => {
                      field.onChange(e.target.checked)
                      if (e.target.checked && form.getValues('due_date')) {
                        const dueDate = form.getValues('due_date')
                        if (dueDate) {
                          form.setValue('recurring_day_of_month', dueDate.getDate())
                        }
                      }
                    }}
                    className="h-5 w-5 rounded border-input accent-primary"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          
          {form.watch('is_monthly_recurring') && (
            <FormField
              control={form.control}
              name="recurring_day_of_month"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dia do Mês para Cobrança</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      placeholder="Ex: 15"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                    />
                  </FormControl>
                  <FormDescription>
                    Todo mês, um novo pagamento será gerado para o dia {field.value || '...'} quando você marcar o atual como pago.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        
        {/* Extra Info (Collapsible) */}
        <Collapsible open={extrasOpen} onOpenChange={setExtrasOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" type="button" className="w-full justify-between">
              <span>Informações Extras</span>
              {extrasOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="invoice_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número da Nota Fiscal</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: NF-12345" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Informações adicionais, número do comprovante, etc."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CollapsibleContent>
        </Collapsible>
      </form>
    </Form>
  )
  
  const FooterButtons = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
      >
        Cancelar
      </Button>
      <Button
        type="submit"
        onClick={form.handleSubmit(onSubmit)}
        disabled={createPayment.isPending || updatePayment.isPending}
      >
        {createPayment.isPending || updatePayment.isPending ? 'Salvando...' : 'Salvar'}
      </Button>
    </>
  )
  
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle>
              {isEditing ? 'Editar Pagamento' : 'Registrar Pagamento'}
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-4">
            {FormContent}
          </div>
          <DrawerFooter className="flex-row gap-2">
            {FooterButtons}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Pagamento' : 'Registrar Pagamento'}
          </DialogTitle>
        </DialogHeader>
        {FormContent}
        <DialogFooter>
          {FooterButtons}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
